import React, { useState } from 'react';
import FilterAndSortBar from '../components/FilterAndSortBar';
import { ProductCardProps } from '../../home/types/types';
import ProductsBlock from '../components/ProductsBlock';
import Breadcrumbs from '../../components/breadcrumb/Breadcrumb';
import Tabs from '../../components/tabs/Tabs';
import { TabProps } from '../../components/tab/Tab';
import { FilterItem } from '../components/Filter';
import { getProducts } from '../services/EcommerceService';
import { useTranslations } from '../../localization/useTranslations';
import useViewItemListEvent from '../../hooks/useViewItemListEvent';

export interface GenderPageProps {
  products: ProductCardProps[];
  genderDetails: {
    name: string;
    description: string;
    id: string;
    slug: string;
  };
  filters?: Array<FilterItem>;
  tabs?: TabProps[];
}

const GenderPage: React.FC<GenderPageProps> = ({ products, genderDetails, filters, tabs }) => {
  const [productItems, setProductItems] = useState<ProductCardProps[]>(products ?? []);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [loading, setLoading] = useState(false);
  const [hasNext, setHasNext] = useState(products.length >= itemsPerPage);
  const translations = useTranslations();

  useViewItemListEvent('view_item_list', genderDetails.id, genderDetails.name, productItems);

  const handleDropdownChange = async (item: {
    label: string;
    sortBy: 'id' | 'price_amount' | 'created_at';
    sortOrder: 'asc' | 'desc';
  }) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sortBy', item.sortBy);
    url.searchParams.set('sortOrder', item.sortOrder);
    window.location.href = url.href;
  };

  const handleFilterChange = (
    selectedFilters: {
      name: string;
      values: string[];
    }[]
  ) => {
    const url = new URL(window.location.href);

    if (selectedFilters.length === 0) {
      const keysToDelete: string[] = [];
      url.searchParams.forEach((_, key) => {
        if (key.startsWith('attributes[')) {
          keysToDelete.push(key);
        }
      });

      keysToDelete.forEach((key) => {
        url.searchParams.delete(key);
      });
      window.location.href = url.href;
    } else {
      selectedFilters.forEach((filter) => {
        if (filter.values.length > 0) {
          url.searchParams.set(`attributes[${filter.name}]`, filter.values.join(','));
        } else {
          url.searchParams.delete(`attributes[${filter.name}]`);
        }
      });

      window.location.href = url.href;
    }
  };

  const handleTabClick = (link: string) => {
    const url = new URL(window.location.href);
    const queryParam = link.split('=')[0];
    const queryValue = link.split('=')[1];
    url.searchParams.set(queryParam, queryValue);

    const newUrl = url.toString().replace(/\/$/, '');
    window.location.href = newUrl;
  };

  const handleLoadMore = async () => {
    const genderId = genderDetails.id;
    if (!genderId) {
      console.error('Gender ID is not defined');
      return;
    }

    setLoading(true);

    try {
      const { products, hasNext } = await getProducts({
        genderId: genderId,
        page: currentPage + 1,
        items_per_page: itemsPerPage,
      });

      setHasNext(hasNext);
      setCurrentPage((prevPage) => prevPage + 1);
      setProductItems((prevProducts) => [...prevProducts, ...products]);
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ecom-gender">
      <Breadcrumbs
        firstHasPreIcon
        breadcrumbs={[
          {
            label: translations?.ecommerce?.shopping || 'Shopping',
            path: '/market/',
          },
          {
            label: genderDetails.name,
            path: `/products/${genderDetails.slug}`,
          },
        ]}
      />

      {tabs && tabs.length > 0 && <Tabs tabs={tabs} hasBottomBorder onTabClick={handleTabClick} />}

      <FilterAndSortBar
        onDropdownChange={handleDropdownChange}
        onFilterChange={handleFilterChange}
        filters={filters}
        filterName={genderDetails.name}
        hideFilter
      />

      {productItems.length > 0 && (
        <ProductsBlock
          products={productItems}
          button={
            hasNext
              ? {
                  label: translations?.ecommerce?.loadMore || 'Load more',
                  onClick: () => handleLoadMore(),
                  loading: loading,
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default GenderPage;
